import { useCallback, useEffect, useRef, useState } from "react";
import { getJson } from "../api";
import { formatText, type Label, type Locale } from "../texts";

// R8 · AR15 (C08 1.14.8, C06): opciones de un selector de datos que crecen (empresas, personas) buscadas en el servidor.
// La consulta sale 50 ms después de la última tecla; solo la respuesta de la última búsqueda se muestra.

/** Espera tras la última tecla antes de consultar al servidor (C06, §3 «Buscar»). */
export const searchDelay = 50;

export type ServerOptions<T> = {
  items: T[];
  total: number;
  /** La búsqueda vigente todavía no respondió. */
  loading: boolean;
  /** El servicio no respondió: el menú lo dice (nunca «sin resultados», H-3). */
  failed: boolean;
  /** El texto ya enviado al servidor (sin espacios al borde). */
  query: string;
  /** Llamar en cada cambio del buscador; consulta 50 ms después de la última llamada. */
  search: (text: string) => void;
};

/**
 * Lee `{ items, total }` del BFF con la búsqueda vigente. `build` arma la dirección con el texto buscado (o null: no
 * pedir todavía, p. ej. con la ventana cerrada).
 */
export function useServerOptions<T>(build: (query: string) => string | null, delay = searchDelay): ServerOptions<T> {
  const [query, setQuery] = useState("");
  const [result, setResult] = useState<{ url: string; items: T[]; total: number; failed: boolean } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const url = build(query);

  useEffect(() => {
    if (!url) return;
    let live = true;
    void getJson<{ items: T[]; total: number }>(url).then((reply) => {
      if (!live) return;
      setResult(
        reply.status === 200 && reply.body
          ? { url, items: reply.body.items, total: reply.body.total, failed: false }
          : { url, items: [], total: 0, failed: true },
      );
    });
    return () => {
      live = false;
    };
  }, [url]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const search = useCallback(
    (text: string) => {
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => setQuery(text.trim()), delay);
    },
    [delay],
  );

  const current = result && result.url === url ? result : null;
  return {
    items: current?.items ?? [],
    total: current?.total ?? 0,
    loading: !!url && !current,
    failed: current?.failed ?? false,
    query,
    search,
  };
}

/**
 * La línea al final del menú: «Buscando…», «No pudimos buscar…», «Sin resultados» o, si hay más de los que se ven,
 * «20 de 154 · escribe para encontrar a alguien». Null si se ve todo.
 */
export function optionsNote(
  L: Label,
  locale: Locale,
  list: Pick<ServerOptions<unknown>, "items" | "total" | "loading" | "failed">,
) {
  if (list.loading) return L("ADM_SEARCHING");
  if (list.failed) return L("ADM_SEARCH_FAILED");
  if (list.items.length === 0) return L("ADM_AI_NO_RESULTS");
  if (list.total > list.items.length)
    return formatText(
      L("ADM_SEARCH_MORE"),
      list.items.length.toLocaleString(locale),
      list.total.toLocaleString(locale),
    );
  return null;
}
